import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Printer, ArrowLeft, Theater } from "lucide-react";
import { fmtDate, fmtTime, fmtTimeRange } from "@/lib/format";

export default function CallSheet() {
  const { rehearsalId } = useParams();
  const [rehearsal, setRehearsal] = useState(null);
  const [users, setUsers] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [production, setProduction] = useState(null);

  useEffect(() => {
    (async () => {
      const [r, u, s, prods] = await Promise.all([
        api.get(`/rehearsals/${rehearsalId}`),
        api.get("/users"),
        api.get("/scenes"),
        api.get("/productions"),
      ]);
      // /rehearsals/{id} returns { rehearsal, calls }
      const reh = { ...r.data.rehearsal, calls: r.data.calls || [] };
      setRehearsal(reh);
      setUsers(u.data);
      setScenes(s.data);
      setProduction(prods.data.find((p) => p.id === reh.production_id) || null);
    })();
  }, [rehearsalId]);

  if (!rehearsal) return <div className="p-8 text-zinc-400">Loading call sheet…</div>;

  const calls = rehearsal.calls || [];
  const sceneList = (rehearsal.scene_ids || []).map((id) => scenes.find((s) => s.id === id)).filter(Boolean);

  const called = calls.filter((c) => c.status === "called");
  const hold = calls.filter((c) => c.status === "hold");
  const off = calls.filter((c) => c.status === "off");

  const rowUser = (uid) => users.find((u) => u.id === uid);

  return (
    <div className="min-h-screen bg-white text-black">
      {/* Screen-only header */}
      <div className="print:hidden bg-[#0A0A0A] text-white border-b border-white/10">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-6 py-3">
          <Link to="/calendar" className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white">
            <ArrowLeft className="w-4 h-4" /> Back to calendar
          </Link>
          <button
            data-testid="print-call-sheet-btn"
            onClick={() => window.print()}
            className="h-9 px-3 rounded font-semibold text-sm inline-flex items-center gap-1.5"
            style={{ backgroundColor: "#F59E0B", color: "#0A0A0A" }}
          >
            <Printer className="w-4 h-4" /> Print / Save as PDF
          </button>
        </div>
      </div>

      {/* Sheet body */}
      <div className="max-w-4xl mx-auto px-8 py-10 print:py-6 print:px-6 print:max-w-none" data-testid="call-sheet-body">
        {/* Header */}
        <header className="flex items-start justify-between pb-5 border-b-2 border-black">
          <div className="flex items-start gap-4">
            {production?.theater_logo_url && (
              <img src={production.theater_logo_url} alt={production.theater_name || "Theatre"} className="w-16 h-16 object-contain rounded border border-zinc-300 p-1 bg-white" />
            )}
            <div>
              <div className="inline-flex items-center gap-2 uppercase tracking-[0.28em] text-[10px] text-zinc-500 mb-2"><Theater className="w-3 h-3" /> Call sheet</div>
              {production?.theater_name && <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">{production.theater_name}</div>}
              <h1 className="font-display text-4xl leading-none">{production?.title || "Production"}</h1>
              {production?.tagline && <p className="mt-2 text-sm text-zinc-600">{production.tagline}</p>}
            </div>
          </div>
          <div className="text-right text-xs">
            {production?.director && <div><span className="uppercase tracking-widest text-zinc-500">Director</span> · {production.director}</div>}
            {production?.venue && <div><span className="uppercase tracking-widest text-zinc-500">Venue</span> · {production.venue}</div>}
          </div>
        </header>

        {/* Rehearsal at a glance */}
        <section className="grid grid-cols-4 gap-4 mt-6">
          <SheetCell label="Date">{fmtDate(rehearsal.date)}</SheetCell>
          <SheetCell label="Time">{fmtTimeRange(rehearsal.start_time, rehearsal.end_time)}</SheetCell>
          <SheetCell label="Location">{rehearsal.location || "—"}</SheetCell>
          <SheetCell label="Rehearsal">{rehearsal.title}</SheetCell>
        </section>

        {rehearsal.notes && (
          <section className="mt-6">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Notes</div>
            <p className="text-sm leading-relaxed">{rehearsal.notes}</p>
          </section>
        )}

        {sceneList.length > 0 && (
          <section className="mt-6">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Scenes / Songs</div>
            <ul className="text-sm space-y-1">
              {sceneList.map((s) => (
                <li key={s.id} className="flex gap-3">
                  <span className="text-zinc-500 w-14 shrink-0">{s.is_song ? "Song" : "Scene"}</span>
                  <span>{s.act ? `Act ${s.act} · ` : ""}{s.name}{s.pages ? ` · pp. ${s.pages}` : ""}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Calls table */}
        <section className="mt-8">
          <div className="flex items-baseline justify-between border-b border-zinc-300 pb-1 mb-3">
            <h2 className="font-display text-2xl">Calls</h2>
            <div className="text-xs text-zinc-500">{called.length} called · {hold.length} hold · {off.length} off</div>
          </div>
          {calls.length === 0 ? (
            <div className="text-sm text-zinc-500 italic">No calls scheduled.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-black">
                  <th className="py-1 pr-3 w-24">Call</th>
                  <th className="py-1 pr-3">Name</th>
                  <th className="py-1 pr-3">Role / Character</th>
                  <th className="py-1 pr-3">Notes</th>
                  <th className="py-1 pr-3 w-20 text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                {[...called, ...hold, ...off].map((c) => {
                  const u = rowUser(c.user_id);
                  return (
                    <tr key={c.id} className="border-b border-zinc-200">
                      <td className="py-1.5 pr-3 font-mono">{fmtTime(c.call_time)}</td>
                      <td className="py-1.5 pr-3">{u?.name || "—"}</td>
                      <td className="py-1.5 pr-3 text-zinc-600">{u?.character || u?.role || ""}</td>
                      <td className="py-1.5 pr-3 text-zinc-600">{c.role_note || ""}</td>
                      <td className="py-1.5 pr-3 text-right uppercase text-[10px] tracking-widest">{c.status}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>

        <footer className="mt-10 pt-4 border-t border-zinc-300 flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500">
          <span>{production?.title} · Call sheet</span>
          <span>Generated by StageCall</span>
        </footer>
      </div>

      {/* Print CSS */}
      <style>{`
        @media print {
          @page { margin: 0.5in; }
          body { background: white; }
        }
      `}</style>
    </div>
  );
}

function SheetCell({ label, children }) {
  return (
    <div className="border border-zinc-300 rounded-md p-3">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="mt-1 text-sm">{children}</div>
    </div>
  );
}
